function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { cloneDeep, mergeDeep } from '@0x234/common-util';
import { useQuasar } from 'quasar';
import { reactive } from 'vue';
var globalDialogDefaults = reactive({
  ok: {
    label: 'OK',
    color: 'primary'
  },
  cancel: {
    label: 'Cancel',
    color: 'grey-7',
    flat: true
  }
});

/**
 * Retrieves default settings for dialogs by merging and returning the global dialog configuration.
 *
 * @return {Object} - The default dialog configuration object.
 */
export function getDialogDefaults() {
  return _objectSpread({}, globalDialogDefaults);
}

/**
 * Updates the global dialog defaults configuration
 *
 * @param {Object} newDefaults - Object containing new default values to merge with current defaults
 */
export function setDialogDefaults() {
  var newDefaults = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  Object.assign(globalDialogDefaults, mergeDeep({}, cloneDeep(globalDialogDefaults), newDefaults));
}

/**
 * Manages and provides a set of dialog-related functions for user interaction within an application. It allows creating
 * different types of dialogs such as informational alerts, confirmations, deletion prompts, negative responses, generic
 * prompts, and custom component dialogs.
 *
 * The dialog functions are pre-configured with default settings, and they can be customized using options passed to them.
 *
 * @return {Object} An object containing functions to invoke different types of dialogs:
 */
export function useDialogManager() {
  var $q = useQuasar();

  /**
   * Displays an alert prompt with merged configuration options and returns the user's response.
   *
   * @param {import('quasar').QDialogOptions} [options={}] - An optional configuration object to customize the alert behavior.
   * @return {import('quasar').DialogChainObject}
   */
  function alertInfo() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return prompt(mergeDeep({
      cancel: false
    }, options));
  }

  /**
   * Displays a confirmation dialog with provided options and returns the user's choice.
   *
   * @param {import('quasar').QDialogOptions} [options={}] - An optional configuration object to customize the confirmation dialog behavior.
   * @return {import('quasar').DialogChainObject}
   */
  function promptConfirm() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return prompt(options);
  }

  /**
   * Displays a deletion confirmation dialog with default "Yes, delete it" button text and merges provided options.
   *
   * @param {import('quasar').QDialogOptions} [options={}] - An optional configuration object to customize the deletion prompt behavior.
   * @return {import('quasar').DialogChainObject}
   */
  function promptDelete() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return promptNegative(mergeDeep({
      ok: {
        label: 'Yes, delete it'
      }
    }, options));
  }

  /**
   * Displays a prompt with a default negative response option and merges any provided configuration options.
   *
   * @param {import('quasar').QDialogOptions} [options={}] - An optional configuration object to customize the negative prompt behavior.
   * @return {import('quasar').DialogChainObject}
   */
  function promptNegative() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return prompt(mergeDeep({
      ok: {
        color: 'negative'
      }
    }, options));
  }

  /**
   * Displays a generic dialog with default configuration settings and merges any provided options.
   *
   * @param {import('quasar').QDialogOptions} [options={}] - An optional configuration object to customize the dialog behavior.
   * @return {import('quasar').DialogChainObject}
   */
  function prompt() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return $q.dialog(mergeDeep({}, cloneDeep(globalDialogDefaults), options));
  }

  /**
   * Opens a dialog with a custom component injected into it, allowing for more personalized interactions.
   *
   * @param {Object} component - The Vue component to be rendered inside the dialog.
   * @param {Object} [componentProps={}] - Properties to pass to the component.
   * @param {import('quasar').QDialogOptions} [options={}] - An optional configuration object to customize the dialog behavior.
   * @return {import('quasar').DialogChainObject}
   */
  function openDialogComponent(component) {
    var componentProps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    return $q.dialog(mergeDeep({}, cloneDeep(globalDialogDefaults), options, {
      component,
      componentProps
    }));
  }
  return {
    alertInfo,
    promptConfirm,
    promptDelete,
    promptNegative,
    prompt,
    openDialogComponent
  };
}